import { ContentExtractionType, IContentExtractionDescriptorRuntime } from '@edgebox/sync-core-data-definitions';
import { ClientContentItemEntity } from '@edgebox/sync-core-rest-client';

/**
 * Reading the question-and-answer pairs a page already answers, out of the
 * extraction file the crawl produced for it.
 */

/**
 * One entry of the Q&A pairs extraction file the descriptor for
 * {@link ContentExtractionType.QaPairs} points at. That file is fetched from the
 * descriptor's download URL, so its body is not part of the generated client's
 * typed surface and is described here. `anchor` is an optional discriminated
 * union: a deterministic pair carries `element-path`, an analysed pair carries
 * `line-span`, and a pair written before anchoring carries none. `sourceHeading`
 * is the heading above an analysed answer and rides only on a `model` pair.
 */
export interface ExtractedQaPair {
  question: string;
  answer: string;
  source: 'json-ld' | 'dl' | 'details' | 'heading' | 'model';
  position: number;
  anchor?: { kind: 'element-path'; path: string } | { kind: 'line-span'; lineStart: number; lineEnd: number };
  sourceHeading?: string;
}

/** The external word a page shows for where a Q&A pair came from. */
export function toQaPairSourceLabel(source: ExtractedQaPair['source']): string {
  switch (source) {
    case 'json-ld':
      return 'Structured data';
    case 'dl':
    case 'details':
    case 'heading':
      return 'Page content';
    case 'model':
      return 'Found by analysis';
  }
}

export async function loadQaPairsByExtraction(item: ClientContentItemEntity): Promise<ExtractedQaPair[]> {
  const qaPairsExtractionDescriptor: IContentExtractionDescriptorRuntime | undefined = item.extractions?.[ContentExtractionType.QaPairs];

  const file = await qaPairsExtractionDescriptor?.file?.get?.();
  const downloadUrl = file?.downloadUrl;
  if (!downloadUrl) {
    throw new Error('Q&A pairs extraction file is not available.');
  }

  const response = await fetch(downloadUrl);
  const body: { qaPairs?: ExtractedQaPair[] } = await response.json();
  const pairs = Array.isArray(body.qaPairs) ? body.qaPairs : [];
  return [...pairs].sort((left, right) => left.position - right.position);
}
